/**
 * Dashboard view „CVE“ (DEV-133): CVE → affected products with their
 * vulnerability case → versions and customers.
 *
 * The sections follow where a product stands with the case (see cveModel.ts):
 * Zu bewerten, In Bearbeitung, Abgeschlossen, Ohne Handlungsbedarf. Assessing,
 * the case, the advisory and the mails stay in the triage and on the
 * Benachrichtigungen page; this view links there.
 */
import type { MessageDescriptor } from '@lingui/core';
import { msg, plural } from '@lingui/core/macro';
import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { useEffect, useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import Chip, { ChipList, type ChipTone } from '../../components/Chip';
import { CveTitleLink } from '../../components/CveLink';
import EmptyState from '../../components/EmptyState';
import { fmtDate, fmtDay } from '../../lib/format';
import type {
  AdvisoryStatus, CustomerMailState, CveArea, CveDashboardEntry, CveDashboardProduct, CveDashboardVersion,
  VulnerabilityEventRow,
} from '../../lib/types';
import { CASE_STATUS, clockChip } from '../triage/CasePanel';
import { STATUS_LABELS, STATUS_TONE } from '../triage/eventHelpers';

import { CVE_AREAS, areaGroups, cveSearchTokens, groupCounts, type CveGroup } from './cveModel';
import { ExploitabilityChips } from './ProductCells';
import type { ExploitabilityStore } from './useExploitability';
import { backendLabel, backendLabelList } from '../../lib/backendLabels';

/** Chip labels are translated at render with `i18n._()`. */
const MAIL_STATE: Readonly<Record<CustomerMailState, { label: MessageDescriptor; tone: ChipTone }>> = {
  open: { label: msg`Mail offen`, tone: 'warn' },
  queued: { label: msg`Mail eingereiht`, tone: 'info' },
  failed: { label: msg`Mail fehlgeschlagen`, tone: 'alert' },
  sent: { label: msg`Mail versendet`, tone: 'good' },
};

const ADVISORY: Readonly<Record<AdvisoryStatus | 'MISSING', { label: MessageDescriptor; tone: ChipTone }>> = {
  MISSING: { label: msg`Advisory fehlt`, tone: 'warn' },
  IN_PROGRESS: { label: msg`Advisory in Arbeit`, tone: 'warn' },
  RELEASED: { label: msg`Advisory freigegeben`, tone: 'good' },
};

export const cveSectionId = (area: CveArea): string => `dash-cve-${area}`;

/** Whole days since the oldest KEV hit, for a CVE nobody opened a case for yet. */
function openDays(firstHit: string | null, now: number): number | null {
  if (!firstHit) return null;
  return Math.max(0, Math.floor((now - Date.parse(firstHit)) / 86_400_000));
}

/** Rendered inside components that call `useLingui()`. */
function verdictChip(version: CveDashboardVersion): ReactNode {
  if (version.confirmed) return <Chip tone={STATUS_TONE[version.status]}>{STATUS_LABELS[version.status]}</Chip>;
  if (version.proposal === 'OPEN') return <Chip tone="warn"><Trans>Offen</Trans></Chip>;
  const proposal = STATUS_LABELS[version.proposal];
  return <Chip tone="neutral"><Trans>Vorschlag: {proposal}</Trans></Chip>;
}

function triageLink(eventId: number, versionId: number | null, tab: 'case' | 'products'): string {
  return `/triage?event=${eventId}${versionId ? `&version=${versionId}` : ''}&tab=${tab}`;
}

function CveHead({ group, kev, store, now }: Readonly<{
  group: CveGroup;
  kev: VulnerabilityEventRow['kev'] | undefined;
  store: ExploitabilityStore;
  now: number;
}>): ReactNode {
  const { t } = useLingui();
  const { entry } = group;
  const { customers, deliveries } = groupCounts(group);
  const products = group.products.length;
  const withoutCase = group.products.every((product) => product.case === null);
  const days = withoutCase ? openDays(entry.first_hit_at, now) : null;
  const facts = store.entries.get(entry.cve);
  const nextDeadline = group.nextDeadline ? fmtDate(group.nextDeadline) : null;
  const listedOn = kev?.aufgenommen_am ? fmtDay(kev.aufgenommen_am) : null;
  return (
    <div className="cve-group-head">
      <div className="cve-group-title">
        <h3><CveTitleLink cve={entry.cve} /></h3>
        <ChipList>
          {/* The looked-up facts bring CVSS with its rating; the stored score stands in until then. */}
          {facts?.state !== 'ready' && entry.base_score !== null ? <Chip tone="neutral">CVSS {entry.base_score}</Chip> : null}
          {store.enabled ? <ExploitabilityChips entry={facts} /> : null}
          {group.overdue ? <Chip tone="alert"><Trans>Meldefrist überschritten</Trans></Chip> : null}
          {!group.overdue && nextDeadline ? <Chip tone="neutral"><Trans>Nächste Frist {nextDeadline}</Trans></Chip> : null}
          {days !== null ? (
            <Chip tone="warn" title={t`Ältester KEV-Treffer ohne bestätigte Produktbewertung`}>
              {days < 1
                ? <Trans>KEV-Treffer seit heute offen</Trans>
                : <Plural value={days} one="KEV-Treffer seit # Tag offen" other="KEV-Treffer seit # Tagen offen" />}
            </Chip>
          ) : null}
        </ChipList>
      </div>
      <div className="prod">
        {[
          kev?.sources.filter(Boolean).join(', ') || null,
          listedOn ? t`aufgenommen ${listedOn}` : null,
          t`${plural(products, { one: '# Produkt', other: '# Produkte' })} · ${plural(customers, { one: '# Kunde', other: '# Kunden' })} · ${plural(deliveries, { one: '# Auslieferung', other: '# Auslieferungen' })}`,
        ].filter(Boolean).join(' · ')}
      </div>
      {entry.description ? <div className="prod cve-description" title={entry.description}>{entry.description}</div> : null}
    </div>
  );
}

function CaseSummary({ product }: Readonly<{ product: CveDashboardProduct }>): ReactNode {
  const { i18n } = useLingui();
  const { case: item, mails } = product;
  if (item === null) return null;
  const { sent, total, queued, failed } = mails;
  const status = CASE_STATUS[item.status];
  const advisory = ADVISORY[item.advisory_status ?? 'MISSING'];
  return (
    <ChipList>
      <Chip tone={status.tone}>{status.label}</Chip>
      {item.clocks.map((clock) => {
        const chip = clockChip(clock);
        return <Chip key={clock.key} tone={chip.tone} title={backendLabel(clock.label)}>{clock.key}: {chip.label}</Chip>;
      })}
      <Chip tone={advisory.tone}>{i18n._(advisory.label)}</Chip>
      {total > 0 ? (
        <Chip tone={sent === total ? 'good' : 'neutral'}><Trans>{sent} / {total} Mails versendet</Trans></Chip>
      ) : null}
      {queued > 0 ? <Chip tone="info"><Trans>{queued} eingereiht</Trans></Chip> : null}
      {failed > 0 ? <Chip tone="alert"><Trans>{failed} fehlgeschlagen</Trans></Chip> : null}
      {item.contradiction ? <Chip tone="alert"><Trans>Widerspruch</Trans></Chip> : null}
    </ChipList>
  );
}

function ProductRow({ product, area }: Readonly<{ product: CveDashboardProduct; area: CveArea }>): ReactNode {
  const { t, i18n } = useLingui();
  const linked = product.versions.find((version) => version.event_id !== null);
  const openItems = product.open_items.join(' · ');
  const versionCount = product.versions.length;
  const customerCount = product.customers.length;
  const missing = backendLabelList(product.case?.missing ?? []);
  return (
    <li className="cve-product">
      <div className="cve-product-head">
        <strong>{product.product_name}</strong>
        <CaseSummary product={product} />
        <div className="spacer" />
        {linked?.event_id ? (
          <Link className="ghost small"
            to={triageLink(linked.event_id, linked.version_id, area === 'assess' ? 'products' : 'case')}>
            {area === 'assess' ? <Trans>Bewerten</Trans> : <Trans>Schwachstellenfall</Trans>}
          </Link>
        ) : null}
        {product.case ? <Link className="ghost small" to={`/?view=fristen&case=${product.case.id}`}><Trans>Fristen</Trans></Link> : null}
      </div>
      {area === 'in_progress' && product.open_items.length > 0 ? (
        <p className="hint"><Trans>Offen: {openItems}</Trans></p>
      ) : null}
      {area === 'in_progress' && product.case && product.case.missing.length > 0 ? (
        <p className="hint"><Trans>Für die nächste Meldung fehlt: {missing}</Trans></p>
      ) : null}
      <details className="cve-details">
        <summary><Plural value={versionCount} one="# Version" other="# Versionen" /></summary>
        <ul className="cve-list">
          {product.versions.map((version) => {
            const deliveries = version.instance_count;
            return (
            <li key={version.version_id}>
              <span>{version.version_nr ?? `#${version.version_id}`}</span>
              {version.custom ? <Chip tone="info"><Trans>Kundenversion</Trans></Chip> : null}
              {verdictChip(version)}
              {version.needs_review ? <Chip tone="warn"><Trans>Erneut prüfen</Trans></Chip> : null}
              <span className="muted">
                <Plural value={deliveries} one="# Auslieferung" other="# Auslieferungen" />
              </span>
              {version.event_id ? (
                <Link className="link" to={triageLink(version.event_id, version.version_id, 'products')}><Trans>Triage</Trans></Link>
              ) : null}
            </li>
            );
          })}
        </ul>
      </details>
      <details className="cve-details">
        <summary><Plural value={customerCount} one="# Kunde" other="# Kunden" /></summary>
        <ul className="cve-list">
          {product.customers.map((customer) => (
            <li key={customer.customer_id ?? customer.name}>
              <details>
                <summary>
                  {customer.name ?? t`Unbekannter Kunde`}
                  {customer.country_iso ? <span className="muted"> · {customer.country_iso}</span> : null}
                  {customer.is_eu ? <> <Chip tone="eu" title={t`Kunde in der EU: CRA-Meldepflichten gelten`}>EU</Chip></> : null}
                  {customer.notification ? (
                    <> <Chip tone={MAIL_STATE[customer.notification].tone}>{i18n._(MAIL_STATE[customer.notification].label)}</Chip></>
                  ) : null}
                </summary>
                <ul className="cve-list">
                  {customer.instances.map((instance) => {
                    const versionNr = instance.version_nr ?? '—';
                    return (
                    <li key={instance.instance_id}>
                      <span>S/N {instance.serial ?? instance.instance_id}</span>
                      <span className="muted"><Trans>Version {versionNr}</Trans></span>
                      <Chip tone={STATUS_TONE[instance.status]}>{STATUS_LABELS[instance.status]}</Chip>
                      {instance.deviates ? <Chip tone="warn" title={t`Eigene Instanzbewertung`}><Trans>abweichend</Trans></Chip> : null}
                    </li>
                    );
                  })}
                </ul>
              </details>
            </li>
          ))}
        </ul>
      </details>
    </li>
  );
}

export interface CveViewProps {
  entries: readonly CveDashboardEntry[];
  events: readonly VulnerabilityEventRow[];
  search: string;
  store: ExploitabilityStore;
  /** When the payload was built: the reference for „seit … offen“. */
  generatedAt: string;
}

export default function CveView({ entries, events, search, store, generatedAt }: Readonly<CveViewProps>): ReactNode {
  const { t, i18n } = useLingui();
  const now = Date.parse(generatedAt);
  const tokens = useMemo(() => cveSearchTokens(search), [search]);
  const kevByCve = useMemo(() => new Map(events.map((event) => [event.cve.id?.toUpperCase() ?? '', event.kev])), [events]);
  const componentsByCve = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const event of events) {
      const key = event.cve.id?.toUpperCase() ?? '';
      map.set(key, [...(map.get(key) ?? []), event.komponente.name ?? '']);
    }
    return map;
  }, [events]);
  const sections = useMemo(
    () => CVE_AREAS.map((section) => ({ ...section, groups: areaGroups(entries, section.area, tokens, componentsByCve) })),
    [entries, tokens, componentsByCve],
  );

  // EPSS is looked up lazily, for the CVEs on screen only.
  const shown = useMemo(() => [...new Set(sections.flatMap((s) => s.groups.map((g) => g.entry.cve)))], [sections]);
  const { request } = store;
  useEffect(() => { request(shown); }, [request, shown]);

  if (entries.length === 0) {
    return (
      <section className="card dash-worklist">
        <EmptyState title={t`Keine KEV-Treffer`}>
          <Trans>Sobald ein KEV-Scan eine Komponente trifft, erscheint die CVE hier.</Trans>
        </EmptyState>
      </section>
    );
  }
  if (sections.every((section) => section.groups.length === 0)) {
    return <section className="card dash-worklist"><EmptyState title={t`Nichts passt zur Suche.`} /></section>;
  }

  return (
    <>
      {sections
        .filter((section) => section.groups.length > 0 || section.empty !== null)
        .map((section) => {
          const products = section.groups.reduce((sum, group) => sum + group.products.length, 0);
          const cves = section.groups.length;
          return (
            <section key={section.area} id={cveSectionId(section.area)}
              className={`card dash-worklist dash-section${section.groups.length === 0 ? ' is-empty' : ''}`}
              aria-labelledby={`${cveSectionId(section.area)}-title`}>
              <div className="card-head table-head">
                <h2 id={`${cveSectionId(section.area)}-title`}>{i18n._(section.title)}</h2>
                <span className="table-count">
                  {cves === 0
                    ? section.empty && i18n._(section.empty)
                    : t`${plural(cves, { one: '# CVE', other: '# CVEs' })} · ${plural(products, { one: '# Produkt', other: '# Produkte' })}`}
                </span>
              </div>
              {section.groups.length > 0 ? (
                <ul className="cve-groups">
                  {section.groups.map((group) => (
                    <li key={group.entry.cve} className="cve-group">
                      <CveHead group={group} kev={kevByCve.get(group.entry.cve)} store={store} now={now} />
                      <ul className="cve-products">
                        {group.products.map((product) => (
                          <ProductRow key={product.product_type_id ?? product.product_name} product={product}
                            area={section.area} />
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>
          );
        })}
    </>
  );
}
